/**
 * BulkDeleteModal — Modal fuer Bulk-Loeschen via Bulk-Aktion.
 *
 * 2-stufiges Submit (Sicherheitsnetz):
 *  1. Anzahl + Warnung anzeigen -> "Loeschen vorbereiten"
 *  2. "Endgueltig loeschen" -> store.runBulkAction (Soft-Delete)
 *
 * Soft-Delete: Lead-Model hat paranoid:true, deleted_at wird gesetzt.
 * Lead bleibt in DB, ist nur ueber Standard-Queries nicht mehr sichtbar.
 * Audit-Event eventType='deleted' mit actorUserId.
 *
 * CI-Farben: #E9FF57 + #1A1A1A. Loesch-Button mit roter Akzentfarbe.
 */

import { useState } from 'react';
import { AlertTriangle, Trash2, X } from 'lucide-react';
import { useLeadBoard } from '../store/useLeadBoard';
import type { BulkActionResponse } from '../services/leadsApi';

interface Props {
  onClose: () => void;
}

export default function BulkDeleteModal({ onClose }: Props) {
  const selectedLeadIds = useLeadBoard((s) => s.selectedLeadIds);
  const runBulkAction = useLeadBoard((s) => s.runBulkAction);

  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkActionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await runBulkAction({
        leadIds: Array.from(selectedLeadIds),
        action: 'delete',
        payload: {},
      });
      setResult(res);
      if (res.failed.length === 0) {
        setTimeout(() => onClose(), 1500);
      }
    } catch (e: any) {
      setError(e?.message ?? 'Loeschen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg max-w-sm w-full mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-4 border-b border-[#DAD9D9]">
          <h2 className="text-base font-semibold text-[#1A1A1A] flex items-center gap-2">
            <Trash2 className="w-4 h-4" />
            Karten loeschen
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-[#1A1A1A] rounded p-1"
            aria-label="Schliessen"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Achtung: Diese Aktion loescht Karten.</p>
              <p className="text-xs mt-1">
                Die Karten werden ausgeblendet (Soft-Delete), bleiben aber in der Datenbank.
                Wiederherstellung nur durch Admin moeglich.
              </p>
            </div>
          </div>

          {!confirmed ? (
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-[#1A1A1A]">{selectedLeadIds.size}</span>{' '}
              {selectedLeadIds.size === 1 ? 'Karte' : 'Karten'} werden geloescht. Fortfahren?
            </p>
          ) : (
            <>
              <div className="bg-gray-50 border border-[#DAD9D9] rounded p-3 text-sm">
                <p className="text-gray-600">
                  <span className="font-semibold text-[#1A1A1A]">{selectedLeadIds.size}</span>{' '}
                  {selectedLeadIds.size === 1 ? 'Karte wird' : 'Karten werden'} jetzt geloescht.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {error}
                </div>
              )}

              {result && result.failed.length === 0 && (
                <div className="bg-[#E9FF57]/30 border border-[#E9FF57] text-[#1A1A1A] px-3 py-2 rounded text-sm font-medium">
                  {result.successful} {result.successful === 1 ? 'Karte' : 'Karten'} geloescht.
                </div>
              )}

              {result && result.failed.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {result.successful > 0 && <p>{result.successful} erfolgreich geloescht.</p>}
                  <p>{result.failed.length} fehlgeschlagen.</p>
                </div>
              )}
            </>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 p-4 border-t border-[#DAD9D9]">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm rounded border border-[#DAD9D9] text-[#1A1A1A] hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          {!confirmed ? (
            <button
              type="button"
              onClick={() => setConfirmed(true)}
              className="px-4 py-2 text-sm rounded bg-red-600 text-white font-medium hover:bg-red-700"
            >
              Loeschen vorbereiten
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy || result !== null}
              className="px-4 py-2 text-sm rounded bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? 'Loesche...' : 'Endgueltig loeschen'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
